/**
 * Booking evt codes can differ from the voyage key (evtPmsCode) used for itinerary,
 * meals, ports, etc. Normalize booking variants to that key:
 * - Cruise + land: trailing letter after sail date (e.g. SYM260506K → SYM260506,
 *   JAZ260530P → JAZ260530). The letter varies by land/departure package.
 * - Group / contracted: G{digits}-{AAA######}[optional land letter]-{suffix}
 *   Suffix is not only GR (e.g. …-CT). Anything after the voyage segment is ignored.
 * Anything else (including standard codes and other legs, e.g. JAZ260601) is unchanged.
 */

import type { UserData } from '../Models/employee-and-user-data';
import type { TripLeg } from '../Models/trip-leg';

/** Ship + sail-date token without land suffix: AAA###### */
const VOYAGE_CORE = /^[A-Za-z]{3}\d{6}$/i;

/** Read voyage/event code from a leg object (supports AX field name variants). */
export function legVoyageCode(leg: TripLeg | undefined): string | undefined {
  if (!leg) return undefined;
  const extra = leg as TripLeg & Record<string, unknown>;
  const raw =
    leg.evtCode ??
    leg.evtPmsCode ??
    (typeof extra['EvtCode'] === 'string' ? extra['EvtCode'] : undefined) ??
    (typeof extra['EvtPmsCode'] === 'string' ? extra['EvtPmsCode'] : undefined);
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s || undefined;
}

/** Package-level evtPmsCode from /AX/login (supports PascalCase payloads). */
export function axLoginPackageEvtPmsCode(
  data: Record<string, unknown> | undefined
): string {
  if (!data) return '';
  const raw = data['evtPmsCode'] ?? data['EvtPmsCode'];
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Strip one trailing land-package letter after AAA###### (K, P, etc.). */
function stripOptionalLandSuffixFromSegment(segment: string): string {
  const landMatch = /^([A-Za-z]{3}\d{6})[A-Za-z]$/i.exec(segment);
  return landMatch ? landMatch[1] : segment;
}

function voyageFromGroupHyphenFormat(code: string): string | null {
  if (!/^G\d+-/i.test(code)) return null;

  const parts = code.split('-');
  if (parts.length < 2 || !/^G\d+$/i.test(parts[0])) return null;

  const candidate = stripOptionalLandSuffixFromSegment(parts[1]);
  if (!VOYAGE_CORE.test(candidate)) return null;

  return candidate;
}

export function voyagePmsCodeFromBookingEvtCode(bookingEvtCode: string): string {
  const c = bookingEvtCode.trim();
  if (!c) return c;

  const fromGroup = voyageFromGroupHyphenFormat(c);
  if (fromGroup != null) return fromGroup;

  const landMatch = /^([A-Za-z]{3}\d{6})[A-Za-z]$/i.exec(c);
  if (landMatch) return landMatch[1];

  return c;
}

/**
 * Itinerary / meals / ports APIs expect the voyage key, not booking evtCode (…K, G…-…-CT).
 * Prefer active leg evtcode (synced by login/guard), else package evtPmsCode; always normalize.
 */
export function cruiseContentEvtCodeFromSession(user: UserData | undefined): string {
  if (!user) return '';
  const fromLeg = user.evtcode?.trim();
  if (fromLeg) return voyagePmsCodeFromBookingEvtCode(fromLeg);
  const pms = user.evtPmsCode?.trim();
  if (pms) return voyagePmsCodeFromBookingEvtCode(pms);
  return '';
}
